import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Plus, Inbox } from 'lucide-react';
import type { Column, TicketSummary, User } from '@vd/types';
import { KCard, Tooltip, cn } from '@vd/ui';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './BoardColumn.module.css';
import { ColumnDot } from './ColumnDot';
import { cardTime } from '../../lib/When';
import { readLocalThumb } from '../annotations/localThumbs';
import { useOpenCardSession } from '../ticket/cardSessionContext';

export interface BoardColumnProps {
  column: Column;
  tickets: TicketSummary[];
  creatorsById: Map<string, User>;
  membersById: Map<string, User>;
  showThumbnails: boolean;
  activeId: string | null;
  onOpenTicket: (t: TicketSummary) => void;
  onCopyLink: (t: TicketSummary) => void;
  onOpenSource: (t: TicketSummary) => void;
  onStatusClick?: (t: TicketSummary, a: HTMLElement) => void;
  onTypeClick?: (t: TicketSummary, a: HTMLElement) => void;
  onAssignClick?: (t: TicketSummary, a: HTMLElement) => void;
  onOverflowClick?: (t: TicketSummary, a: HTMLElement) => void;
  /** Clicked the "Add ticket" slot at the bottom. Forwards the column id
   * plus the anchor element so the parent can position a popover against
   * the button the user clicked. */
  onAddTicket?: (columnId: string, anchor: HTMLElement) => void;
  /** Clicked the three-dot "More" menu on the column header. Parent opens
   * a popover anchored at the button. */
  onColumnMenu?: (columnId: string, anchor: HTMLElement) => void;
  /** Bulk-select state, threaded to each card. */
  bulkSelection?: Set<string>;
  onBulkToggle?: (ticketId: string, next: boolean) => void;
  /** Tickets currently being processed by a bulk operation — rendered
   * with a dim+spinner overlay until the operation lands. */
  movingTicketIds?: Set<string>;
  /** TOADO-251: project-level bug-intake toggle; the intake note renders
   *  only when the toggle is on AND this column carries the marker. */
  intakeEnabled?: boolean;
  /** The board tour points at this column's head and its Add ticket (TOADO-981): set on the first column only. */
  tourAnchored?: boolean;
}

export function BoardColumn({
  column, tickets, creatorsById, membersById, showThumbnails, activeId,
  onOpenTicket, onCopyLink, onOpenSource, onStatusClick, onTypeClick, onAssignClick, onOverflowClick, onAddTicket, onColumnMenu,
  bulkSelection, onBulkToggle, movingTicketIds, intakeEnabled = false, tourAnchored = false,
}: BoardColumnProps) {
  const navigate = useNavigate();
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const ticketIds = tickets.map((t) => t.id);
  const containsActive = activeId !== null && ticketIds.includes(activeId);

  return (
    <div ref={setNodeRef} data-testid="board-column" data-column-id={column.id} data-column-name={column.name} className={cn(styles.col, (isOver || containsActive) && styles.over)}>
      <div className={styles.head} data-tour-anchor={tourAnchored ? 'board.columns' : undefined}>
        <div className={styles.left}>
          <ColumnDot tone={column.tone} />
          <span className={styles.name}>{column.name}</span>
          <span className={styles.count}>{tickets.length}</span>
          {column.isIntake && intakeEnabled && (
            /* TOADO-251: the entire discoverability story for the intake
               column. A column that silently receives tickets from nowhere
               is confusing; one that explains itself in a click is not. */
            <Tooltip content="Incoming widget bug reports land here by default. Click to change this in project settings.">
              <button
                type="button"
                className={styles.intakeNote}
                onClick={() => navigate(`/projects/${column.projectId}/settings?tab=intake`)}
              >
                <Inbox size={10} />
                intake
              </button>
            </Tooltip>
          )}
        </div>
        <div className={styles.right}>
          {/* Only "More" lives here now — the old hover-only "+" was
              redundant with the always-visible "Add ticket" slot at the
              bottom of the column (same onAddTicket handler). */}
          <Tooltip content="More">
            <button
              className={styles.iconMini}
              aria-label="More"
              onClick={(e) => onColumnMenu?.(column.id, e.currentTarget)}
            >
              <MoreHorizontal size={12} />
            </button>
          </Tooltip>
        </div>
      </div>

      <SortableContext items={ticketIds} strategy={verticalListSortingStrategy}>
        <div className={styles.list}>
          {tickets.map((t) => (
            <SortableCard
              key={t.id}
              ticket={t}
              creator={creatorsById.get(t.createdBy)}
              membersById={membersById}
              column={column}
              showThumbnail={showThumbnails}
              onOpen={onOpenTicket}
              onCopyLink={onCopyLink}
              onOpenSource={onOpenSource}
              onStatusClick={onStatusClick}
              onTypeClick={onTypeClick}
              onAssignClick={onAssignClick}
              onOverflowClick={onOverflowClick}
              isDragging={activeId === t.id}
              bulkSelectable={!!bulkSelection}
              bulkSelected={bulkSelection?.has(t.id)}
              onBulkToggle={onBulkToggle}
              isMoving={movingTicketIds?.has(t.id)}
            />
          ))}
          {tickets.length === 0 && <div className={styles.empty}>Nothing in this column</div>}

          <button
            className={styles.addSlot}
            data-tour-anchor={tourAnchored ? 'board.add-ticket' : undefined}
            onClick={(e) => onAddTicket?.(column.id, e.currentTarget)}
          >
            <Plus size={12} /> Add ticket
          </button>
        </div>
      </SortableContext>
    </div>
  );
}

interface SortableCardProps {
  ticket: TicketSummary;
  creator?: User;
  membersById: Map<string, User>;
  column: Column;
  showThumbnail: boolean;
  isDragging: boolean;
  onOpen: (t: TicketSummary) => void;
  onCopyLink: (t: TicketSummary) => void;
  onOpenSource: (t: TicketSummary) => void;
  onStatusClick?: (t: TicketSummary, a: HTMLElement) => void;
  onTypeClick?: (t: TicketSummary, a: HTMLElement) => void;
  onAssignClick?: (t: TicketSummary, a: HTMLElement) => void;
  onOverflowClick?: (t: TicketSummary, a: HTMLElement) => void;
  bulkSelectable?: boolean;
  bulkSelected?: boolean;
  onBulkToggle?: (ticketId: string, next: boolean) => void;
  isMoving?: boolean;
}

/**
 * One card in a column. dnd-kit re-renders every sortable on the board each
 * time the card being dragged passes over something new, so the card itself
 * is drawn by `CardBody`, which only redraws when its own props change: on a
 * board of a couple of hundred cards, redrawing them all at every step held the
 * drag for whole seconds (Jason, 23 Oct, t-sWrRBH).
 */
function SortableCard({ isDragging, ...card }: SortableCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: card.ticket.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // While this card is being dragged, render a compact placeholder in its
  // original slot (~1/5 the height of a full card). The DragOverlay in
  // BoardView shows the full-size card following the cursor.
  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={styles.placeholder}
        aria-hidden
      />
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CardBody {...card} />
    </div>
  );
}

const CardBody = memo(function CardBody({
  ticket, creator, membersById, column, showThumbnail,
  onOpen, onCopyLink, onOpenSource, onStatusClick, onTypeClick, onAssignClick, onOverflowClick,
  bulkSelectable, bulkSelected, onBulkToggle, isMoving,
}: Omit<SortableCardProps, 'isDragging'>) {
  const openSession = useOpenCardSession();
  return (
    <KCard
      ticket={ticket}
      creator={creator}
      assignees={ticket.assignees.map((id) => membersById.get(id)).filter(Boolean) as User[]}
      column={column}
      showThumbnail={showThumbnail}
      thumbnailUrl={ticket.thumbnailUrl ?? readLocalThumb(ticket.id)}
      onOpen={onOpen}
      onCopyLink={onCopyLink}
      onOpenSource={onOpenSource}
      onStatusClick={onStatusClick}
      onTypeClick={onTypeClick}
      onAssignClick={onAssignClick}
      onOverflowClick={onOverflowClick}
      onSessionClick={openSession}
      formatTime={cardTime}
      bulkSelectable={bulkSelectable}
      bulkSelected={bulkSelected}
      onBulkToggle={onBulkToggle}
      isMoving={isMoving}
    />
  );
});
